import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControl,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Loading } from '../Loading';

interface EntityCreateProps {
  onSubmit: () => void;
}
export const EntityCreate = ({ onSubmit }: EntityCreateProps) => {
  const api = useApi();

  const [entityName, setEntityName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [selectedEntityName, setSelectedEntityName] = useState<string>('');
  const [parentEntityNames, setParentEntityNames] = useState<string[]>([]);

  const createEntity = useMutation(api.createEntityApiV1CreateEntityPost, {
    onSuccess() {
      toast.success(`Created Entity ${entityName} successfully.`);
      onSubmit();
    },
    onError: apiError,
  });

  const { isLoading, data: prepareEntityNames, } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {parent: true}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (prepareEntityNames != null && prepareEntityNames != undefined) {
      setParentEntityNames(prepareEntityNames.data || []);
    }
  }, [prepareEntityNames]);

  const handleSubmit = () => {
    createEntity.mutate({
      entity_name: entityName,
      description: description,
      parent_entity_name: selectedEntityName
    });
  };

  const handleChange = ({ target: { value }}: SelectChangeEvent): void => {
    setSelectedEntityName(value);
  };

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="entity_name"
            label="Entity Name"
            variant="outlined"
            value={entityName}
            onChange={(e) => setEntityName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            label="Description"
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="parent-entity-label">Parent entity name</InputLabel>
            <Select
              labelId="parent-entity-label"
              value={selectedEntityName}
              label="Parent entity name"
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              { parentEntityNames && parentEntityNames.map((item, i) => {
                return <MenuItem value={item} key={item}>{item}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={entityName === ''}>
          Create
        </Button>
      </Box>
    </Box>
  );
};

EntityCreate.displayName = 'Entity Create';
